'use client'

import { useApolloClient, useQuery } from '@apollo/client/react'
import { useShallow } from 'zustand/react/shallow'
import { usePathname, useRouter } from '@/navigation'
import { useSessionStore } from '@/app/store/session'
import {
  NAVIGATION_QUERY,
  NavigationDataType
} from '@/graphql/queries/navigation'
import {
  NavigationInput,
  nextStepPath,
  stepIndex,
  previousStepPath,
  projectTypeOf,
  resolveSteps
} from './steps'

// Navigation of the prospect between the funnel steps, from its backend data and its session
export const useNavigation = () => {
  const router = useRouter()
  const pathname = usePathname()
  const client = useApolloClient()
  const securityToken = useSessionStore((state) => state.contact.securityToken)
  const session = useSessionStore(
    useShallow(({ entryParameters, localAnswers, restoreSession }) => ({
      entryParameters,
      localAnswers,
      restoreSession
    }))
  )
  const { data, loading } = useQuery<NavigationDataType>(NAVIGATION_QUERY, {
    skip: !securityToken
  })

  const input: NavigationInput = { prospect: data, session }
  const navigation = resolveSteps(input)

  // Reads the prospect data again from the backend, once it changed it; the access guard follows the new data
  const refreshProspect = async () => {
    if (!useSessionStore.getState().contact.securityToken) return undefined
    const { data: refreshedData } = await client.query<NavigationDataType>({
      query: NAVIGATION_QUERY,
      fetchPolicy: 'network-only'
    })
    return refreshedData
  }

  // Steps of the prospect read again from the backend and the session, once a step is sent
  const sentNavigation = async () =>
    resolveSteps({
      prospect: await refreshProspect(),
      session: useSessionStore.getState()
    })

  // Opens the step after the current one, once its answer is sent
  const goToNextStep = async () => {
    const nextPath = nextStepPath(await sentNavigation(), pathname)
    if (nextPath) router.push(nextPath)
  }

  // Opens a step from the breadcrumb or the order recap: going back replaces the current step in the history
  const goToStep = (path: string) => {
    if (stepIndex(navigation, path) < stepIndex(navigation, pathname))
      router.replace(path)
    else router.push(path)
  }

  const previousPath = previousStepPath(navigation, pathname)

  // Opens the step before the current one, in place of the current one in the history
  const goToPreviousStep = () => {
    if (previousPath) router.replace(previousPath)
  }

  return {
    navigation,
    loading: loading && !data,
    projectType: projectTypeOf(input),
    previousPath,
    refreshProspect,
    goToNextStep,
    goToPreviousStep,
    goToStep
  }
}
